import { DialogTitle } from "@headlessui/react";
import { Check } from "lucide-react";
import { useSearchParams } from "react-router";
import type { EventFragment } from "~/__generated__/graphql/graphql";
import { PrimaryButton } from "~/components/Button";
import { StepIndicator } from "~/components/plans/components/StepIndicator";
import { Text } from "~/components/Text";
import { CheckoutFooter } from "./CheckoutFooter";
import { CheckoutTextField } from "./CheckoutQuestionStep";
import { useCheckoutSession } from "./CheckoutSession";
import { groupSelectionAvailable, namedGroupSelection } from "./group-checkout";
import { TICKET_VARIATION_PARAM } from "./ticketVariations";

export function CheckoutGuestsStep({ plan, onComplete }: { plan: EventFragment; onComplete: () => void }) {
  const { draft, update, setStep } = useCheckoutSession();
  const [, setSearchParams] = useSearchParams();
  const slots = draft.groupSlots;
  const selection = namedGroupSelection(slots, draft.purchaserSlotID);
  const valid = !!selection && groupSelectionAvailable(slots, plan);
  const hasPurchaser = slots.some(slot => slot.id === draft.purchaserSlotID);
  const hostName = plan.hosts?.find(host => host.primary)?.user.firstName?.trim() || "the host";

  return (
    <div className="flex min-h-0 flex-1 flex-col sm:block">
      <div className="shrink-0">
        <StepIndicator currentStep={2} showTicketStep stepActions={{ 1: () => setStep("tickets") }} />
      </div>
      <div className="-mx-6 mt-[10px] min-h-0 flex-1 overflow-y-auto overscroll-contain px-6 pt-[30px] pb-2 sm:mx-0 sm:overflow-visible sm:px-0 sm:pb-6">
        <Text as={DialogTitle} variant="primary">Who&apos;s coming?</Text>
        <div className="mt-2.5 flex flex-wrap items-center gap-x-3 gap-y-1">
          <Text size="sm" className="min-w-0 flex-1 leading-relaxed">
            Let {hostName} know who’s joining you.
          </Text>
          {hasPurchaser && (
            <button type="button" className="ml-auto min-h-11 shrink-0 cursor-pointer rounded-sm font-sans text-sm font-semibold text-butter-green underline underline-offset-4 transition-opacity hover:opacity-80 hover:decoration-2 focus-visible:outline-2! focus-visible:outline-offset-2 focus-visible:outline-butter-green" onClick={() => update({ purchaserSlotID: "" })}>
              Change your ticket
            </button>
          )}
        </div>
        <fieldset className="mt-5 flex flex-col gap-3">
          <legend className="sr-only">Your group&apos;s tickets</legend>
          {slots.map((slot, index) => {
            const variation = plan.ticketVariations.find(value => value.id === slot.variationID);
            const yours = slot.id === draft.purchaserSlotID;

            return (
              <div key={slot.id} className="overflow-hidden rounded-xl bg-white">
                <div className="flex items-center gap-3 px-3 py-2">
                  <span aria-hidden className="flex size-11 shrink-0 items-center justify-center rounded-lg bg-butter-cream text-xl">
                    {variation?.emoji ?? "🎟️"}
                  </span>
                  <Text weight="bold" className="min-w-0 flex-1 wrap-break-word">
                    {variation?.name}
                  </Text>
                  {yours ? (
                    <span className="inline-flex min-h-8 items-center gap-1.5 rounded-full bg-butter-pink px-3 py-1 font-sans text-[16px] font-semibold text-butter-cream-light">
                      <Check aria-hidden className="size-3.5" /> Yours
                    </span>
                  ) : !hasPurchaser && <label className="relative flex min-h-11 shrink-0 cursor-pointer items-center">
                    <input
                      type="radio"
                      name="purchaser-ticket"
                      aria-label={`Mark ticket ${index + 1} as yours`}
                      className="peer sr-only"
                      checked={yours}
                      onChange={() => {
                        update({ purchaserSlotID: slot.id, ticketVariationID: slot.variationID });
                        setSearchParams(previous => {
                          const next = new URLSearchParams(previous);
                          next.set(TICKET_VARIATION_PARAM, slot.variationID);
                          return next;
                        }, { replace: true, preventScrollReset: true });
                      }}
                    />
                    <span className="inline-flex min-h-8 items-center justify-center gap-1.5 rounded-full border border-butter-pink px-3 py-1 font-sans text-[16px] font-semibold whitespace-nowrap text-butter-pink transition-colors peer-checked:bg-butter-pink peer-checked:text-butter-cream-light peer-focus-visible:border-butter-green">
                      <Check aria-hidden className="size-3.5" />
                      {yours ? "Yours" : "Mark yours"}
                    </span>
                  </label>}
                </div>
                {!yours && (
                  <div className="px-3 pb-3 [&_input]:bg-butter-cream-light/25">
                    <CheckoutTextField
                      compact
                      label={`Name for ticket ${index + 1}`}
                      maxLength={100}
                      value={slot.recipientName}
                      onChange={event => update({
                        groupSlots: slots.map(value => value.id === slot.id ? { ...value, recipientName: event.target.value } : value),
                      })}
                      placeholder="Their name"
                    />
                  </div>
                )}
              </div>
            );
          })}
        </fieldset>
      </div>
      <CheckoutFooter onBack={() => setStep("tickets")} showTerms={false} widePrimary>
        <PrimaryButton type="button" className="text-base!" disabled={!valid} onClick={() => { if (valid) onComplete(); }}>
          Next
        </PrimaryButton>
      </CheckoutFooter>
    </div>
  );
}
